/**
 * Copied from @atlassian/preview-panel-global-objects/src/panels/IFramePreviewPanel.tsx
 * Since we can't use the @atlassian/preview-panel-global-objects package in ai-mate packages
 * as it introduces a circular dependency.
 *
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	memo,
	type ReactNode,
	useEffect,
	useMemo,
	useCallback,
	useState,
	type MemoExoticComponent,
} from 'react';

import AnalyticsWebClient, {
	envType,
	originType,
	tenantType,
} from '@atlassiansox/analytics-web-client';
import { cssMap, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import type { AnalyticsAttributes } from '@atlassian/analytics-bridge';
import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

const getAnalyticsEnv = (hostname: string) => {
	if (hostname.includes('localhost') || hostname.includes('jira-dev')) {
		return envType.DEV;
	}

	return envType.PROD;
};

type Props = {
	analyticsAttributes?: AnalyticsAttributes;
	ari?: string;
	icon: ReactNode;
	iframeUrl: string;
	loadingState?: () => React.ReactNode;
	name: string;
	panelType?: string;
	parentProduct?: string;
	product: string;
	url: string;
};

const styles = cssMap({
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
	},
	body: {
		display: 'flex',
		flexGrow: 1,
		overflowY: 'auto',
	},
	loadingContainer: {
		width: '100%',
		height: '100%',
	},
	iframeHidden: {
		display: 'none',
	},
});

export const getCloudIdFromAri = (ari?: string): string | undefined => {
	if (!ari || typeof ari !== 'string' || !ari.startsWith('ari:cloud:jira:')) {
		return undefined;
	}
	const parts = ari.split(':');

	if (parts.length !== 5) {
		return undefined;
	}

	const cloudId = parts[3];

	if (!cloudId) {
		return undefined;
	}

	return cloudId;
};

export const IFramePreviewPanel: MemoExoticComponent<
	({
		url,
		iframeUrl,
		name,
		icon,
		panelType,
		product,
		parentProduct,
		ari,
		analyticsAttributes,
		loadingState,
	}: Props) => JSX.Element
> = memo(
	({
		url,
		iframeUrl,
		name,
		icon,
		panelType,
		product,
		parentProduct,
		ari,
		analyticsAttributes,
		loadingState,
	}: Props): JSX.Element => {
		const { close } = usePreviewPanelsActions();
		const [analyticsClient, setAnalyticsClient] = useState<AnalyticsWebClient>();
		const cloudId = ari ?? getCloudIdFromAri(ari);
		const { colorMode } = useThemeObserver();
		const [isIframeLoading, setIsIframeLoading] = useState(true);

		useEffect(() => {
			setAnalyticsClient(() => {
				let analyticsClient = new AnalyticsWebClient({
					product,
					env: getAnalyticsEnv(window.location.hostname),
					origin: originType.WEB,
				});

				if (cloudId) {
					analyticsClient?.setTenantInfo(tenantType.CLOUD_ID, cloudId);
				}

				return analyticsClient;
			});
		}, [product, cloudId]);

		// Create iframe URL with theme parameter
		const iframeSrc = useMemo(() => {
			if (!iframeUrl || !colorMode) {
				return iframeUrl;
			}

			const url = new URL(iframeUrl, window.location.origin);
			url.searchParams.set('themeMode', colorMode);
			return url.toString();
		}, [iframeUrl, colorMode]);
		const isLoadingStateAvailable = Boolean(loadingState);

		// Reset loading state when the iframe URL changes
		useEffect(() => {
			if (isLoadingStateAvailable && fg('add_loading_state_in_preview_panel')) {
				setIsIframeLoading(true);
			}
		}, [iframeUrl, isLoadingStateAvailable]);

		const handleIframeLoad = useCallback(() => {
			if (isLoadingStateAvailable && fg('add_loading_state_in_preview_panel')) {
				setIsIframeLoading(false);
			}
		}, [isLoadingStateAvailable]);

		return (
			<PreviewPanel>
				<PreviewPanelHeader
					onClose={close}
					url={url}
					parentProduct={parentProduct}
					panelType={panelType}
					analyticsClient={analyticsClient}
				>
					<PreviewPanelTitle title={name} icon={icon} />
				</PreviewPanelHeader>
				<PreviewPanelBody
					styles={styles.body}
					parentProduct={parentProduct}
					panelType={panelType}
					analyticsClient={analyticsClient}
					{...(fg('add-trigger-point-key-attribute-to-preview-panel') && {
						analyticsAttributes: analyticsAttributes,
					})}
				>
					{isLoadingStateAvailable &&
						isIframeLoading &&
						loadingState &&
						fg('add_loading_state_in_preview_panel') && (
							<div css={styles.loadingContainer}>{loadingState()}</div>
						)}
					<iframe
						src={iframeSrc}
						css={[
							styles.iframe,
							isLoadingStateAvailable &&
								isIframeLoading &&
								fg('add_loading_state_in_preview_panel') &&
								styles.iframeHidden,
						]}
						title={name}
						{...(fg('add_loading_state_in_preview_panel') && { onLoad: handleIframeLoad })}
					/>
				</PreviewPanelBody>
			</PreviewPanel>
		);
	},
);
